if (!customElements.get('milanis-hero')) {
  customElements.define('milanis-hero', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.options = { signal: this.abort.signal };
      this.videoSlot = this.querySelector('.mv-hero__video-slot');
      this.button = this.querySelector('.mv-hero__video-toggle');
      this.mobile = matchMedia('(max-width: 749px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.measureScreen = () => {
        const top = Math.max(0, this.getBoundingClientRect().top + window.scrollY);
        this.style.setProperty('--mv-screen-top', top + 'px');
      };
      this.measureScreen();
      window.addEventListener('resize', this.measureScreen, this.options);
      this.headerObserver = new ResizeObserver(this.measureScreen);
      document.querySelectorAll('.shopify-section-group-header-group, .section-header').forEach(header => this.headerObserver.observe(header));
      requestAnimationFrame(() => { if (this.isConnected) this.measureScreen(); });
      this.visible = true;
      this.userPaused = false;
      this.index = 0;
      this.retireTimers = new Set();
      this.button.addEventListener('click', () => {
        if (!this.video) this.mount();
        if (!this.video) return;
        this.userPaused = !this.video.paused;
        if (this.userPaused) this.video.pause();
        else this.play();
      }, this.options);
      this.mobile.addEventListener('change', () => this.selectVideo(), this.options);
      this.reduced.addEventListener('change', () => {
        this.selectVideo();
      }, this.options);
      this.addEventListener('pointerdown', () => this.autoPlay(), this.options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.video?.pause();
        else this.autoPlay();
      }, this.options);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        if (!this.visible) this.video?.pause();
        else this.autoPlay();
      });
      this.observer.observe(this);
      this.selectVideo();
    }
    selectedTemplate() {
      return this.templates?.[this.index] || null;
    }
    selectVideo() {
      clearTimeout(this.rotationTimer);
      this.rotationTimer = null;
      this.videoSlot.querySelectorAll('video').forEach(video => video.pause());
      this.retireTimers.forEach(clearTimeout);
      this.retireTimers.clear();
      this.video = null;
      this.prepared = null;
      this.index = 0;
      this.templates = [...this.querySelectorAll(this.mobile.matches ? 'template[data-video="mobile"]' : 'template[data-video="desktop"]')];
      this.videoSlot.replaceChildren();
      this.button.hidden = !this.selectedTemplate();
      this.button.textContent = 'Play video';
      if (!this.reduced.matches && !this.button.hidden) { this.mount(); this.autoPlay(); }
    }
    createVideo(template) {
      const video = template.content.querySelector('video')?.cloneNode(true);
      if (!video) return null;
      // Remove the Shopify thumbnail before inserting the element into the page.
      video.removeAttribute('poster');
      video.removeAttribute('autoplay');
      video.removeAttribute('controls');
      video.muted = true;
      video.defaultMuted = true;
      video.volume = 0;
      video.playsInline = true;
      video.controls = false;
      video.loop = true;
      video.preload = 'auto';
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.disablePictureInPicture = true;
      video.disableRemotePlayback = true;
      return video;
    }
    mount() {
      clearTimeout(this.rotationTimer);
      this.rotationTimer = null;
      const template = this.selectedTemplate();
      if (!template) return;
      const previous = this.video;
      const video = this.prepared?.index === this.index ? this.prepared.video : this.createVideo(template);
      this.prepared = null;
      if (!video) return;
      this.video = video;
      this.videoSlot.append(video);
      let previousRetired = false;
      video.addEventListener('playing', () => {
        if (this.video !== video) return;
        video.classList.add('mv-hero__video--ready');
        this.button.textContent = 'Pause video';
        this.scheduleRotation();
        if (previous && !previousRetired) {
          previousRetired = true;
          previous.pause();
          const timer = setTimeout(() => { previous.remove(); this.retireTimers.delete(timer); }, 400);
          this.retireTimers.add(timer);
        }
        this.prepareNext();
      }, this.options);
      video.addEventListener('ended', () => {
        if (this.video !== video || !this.mobile.matches || this.templates.length < 2) return;
        this.index = (this.index + 1) % this.templates.length;
        this.mount();
        this.autoPlay();
      }, this.options);
      video.addEventListener('pause', () => {
        if (this.video === video) {
          this.button.textContent = 'Play video';
          clearTimeout(this.rotationTimer);
          this.rotationTimer = null;
        }
      }, this.options);
      video.addEventListener('error', () => {
        video.classList.remove('mv-hero__video--ready');
        if (this.video === video) this.button.textContent = 'Play video';
      }, this.options);
    }
    prepareNext() {
      if (!this.mobile.matches || this.templates.length < 2 || this.prepared) return;
      const index = (this.index + 1) % this.templates.length;
      const video = this.createVideo(this.templates[index]);
      if (!video) return;
      this.prepared = { index, video };
      video.load();
    }
    scheduleRotation() {
      if (!this.mobile.matches || this.templates.length < 2 || this.rotationTimer ||
          !this.visible || document.hidden || this.reduced.matches || this.userPaused) return;
      this.rotationTimer = setTimeout(() => {
        this.rotationTimer = null;
        if (!this.isConnected || !this.visible || document.hidden || this.userPaused || this.reduced.matches) return;
        this.index = (this.index + 1) % this.templates.length;
        this.mount();
        this.autoPlay();
      }, 6500);
    }
    play() {
      this.video?.play()?.catch(() => { this.button.textContent = 'Play video'; });
    }
    autoPlay() {
      if (this.visible && !document.hidden && !this.reduced.matches && !this.userPaused) this.play();
    }
    disconnectedCallback() {
      clearTimeout(this.rotationTimer);
      this.rotationTimer = null;
      this.videoSlot?.querySelectorAll('video').forEach(video => video.pause());
      if (this.prepared) {
        this.prepared.video.removeAttribute('src');
        this.prepared.video.querySelectorAll('source').forEach(source => source.remove());
        this.prepared.video.load();
      }
      this.prepared = null;
      this.retireTimers?.forEach(clearTimeout);
      this.observer?.disconnect();
      this.headerObserver?.disconnect();
      this.abort?.abort();
      this.abort = null;
    }
  });
}
